import { ReactNode, useMemo } from "react";

import { TestingCoverage, TestingCoverageItem } from "@common/models/Analytics.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { getDimensionMap } from "@main/pages/Release/helpers/dimensionMapper";
import { getMetricDimensionConfig } from "@main/pages/Release/helpers/metricDimensionConfig";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export interface CoverageDimension {
  key: string;
  label: string;
  icon: ReactNode;
  score?: number | null;
  scoreTooltip: string;
  items: TestingCoverageItem[];
}

export default function useTestingCoverageData() {
  const [searchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version") ?? "";

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { analyticsService } = useAnalyticsDeps();

  async function getTestingCoverageDetails(signal?: AbortSignal): Promise<TestingCoverage | undefined> {
    if (!selectedWorkspace || !selectedApp || !selectedVersion) return undefined;

    const { data } = await analyticsService.getTestingCoverageDetails(
      selectedWorkspace.id,
      selectedApp.id,
      selectedVersion,
      signal,
    );
    return data;
  }

  const { data, isLoading } = useQuery(
    ["testingCoverage", { selectedApp, selectedVersion }],
    ({ signal }) => getTestingCoverageDetails(signal),
    {
      enabled: Boolean(selectedVersion),
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const dimensions: CoverageDimension[] = useMemo(() => {
    if (!data) return [];

    return Object.entries(getDimensionMap(data.sessions))
      .filter(([, { items }]) => items.length > 0)
      .map(([key, { score, items }]) => {
        const config = getMetricDimensionConfig(key);

        return {
          key,
          label: config.label,
          icon: config.icon,
          score,
          scoreTooltip: `Dimension score — ${formatScore(score)}`,
          items,
        };
      });
  }, [data]);

  return {
    dimensions,
    isLoading,
  };
}

function formatScore(score?: number | null): string {
  if (score == null) return "n/a";
  return score >= 10 ? score.toFixed(0) : score.toFixed(1);
}
